import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Button, Modal } from 'react-bootstrap';
import FilterPanel from '@spark/ui-components/components/FilterPanel';
import TanStackTable from '@spark/ui-components/components/TanStackTable/TanStackTable';
import TruncatedText from '@spark/ui-components/components/TanStackTable/components/TruncatedText';
import { notification } from '@spark/ui-components/utils/notification';
import { listarAuditoriaProgramacaoHavan } from '../../services/programacaoHavanService';
import { formatarDataBr, formatarDataHoraBr } from '../utils/dataBr';
import {
  TIPOS_ACAO_AUDITORIA,
  formatarValorAuditoria,
  periodoParaApi,
  resumirDetalhe,
  rotuloCampo,
  rotuloTipoAcao
} from '../utils/auditoria';

// Auditoria geral da programação Havan, no mesmo formato da "Auditoria Geral da Descarga":
// filtros, uma linha por campo alterado, paginação no servidor e exportação para Excel.

const PAGINACAO_INICIAL = { pageIndex: 0, pageSize: 25 };

export const filtrosAuditoria = [
  {
    id: 'tipo_acao',
    type: 'select',
    label: 'Ação',
    placeholder: 'Todas',
    options: Object.entries(TIPOS_ACAO_AUDITORIA).map(([value, label]) => ({ value, label }))
  },
  { id: 'numero_carga', type: 'text', label: 'Carga', placeholder: 'Digite o número da carga' },
  { id: 'campo', type: 'text', label: 'Campo', placeholder: 'Ex.: cpf, placa, protocolo' },
  { id: 'usuario', type: 'text', label: 'Usuário', placeholder: 'Digite o usuário' },
  { id: 'data_inicio', type: 'datePicker', label: 'De', placeholder: 'Selecione o início', showTimeSelect: true },
  { id: 'data_fim', type: 'datePicker', label: 'Até', placeholder: 'Selecione o fim', showTimeSelect: true }
];

const texto = maxLength => ({ getValue }) => <TruncatedText value={getValue() || '-'} maxLength={maxLength} />;

// Campo alterado: valor anterior e novo; demais ações: "-" (o resumo vai na descrição).
const valorDoCampo = chave => registro => (registro.campo ? formatarValorAuditoria(registro[chave]) : '-');

export const colunasAuditoria = [
  { id: 'data_hora', accessorKey: 'data_hora', header: 'Data e hora', size: 150, cell: ({ getValue }) => <span style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{formatarDataHoraBr(getValue()) || '-'}</span>, copyValue: ({ getValue }) => formatarDataHoraBr(getValue()) },
  { id: 'tipo_acao', accessorFn: registro => rotuloTipoAcao(registro.tipo_acao), header: 'Ação', size: 120, cell: texto(16) },
  { id: 'numero_carga', accessorKey: 'numero_carga', header: 'Carga', size: 110, cell: texto(14) },
  { id: 'campo', accessorFn: registro => rotuloCampo(registro.campo), header: 'Campo', size: 190, cell: texto(26) },
  { id: 'valor_anterior', accessorFn: valorDoCampo('valor_anterior'), header: 'Valor anterior', size: 200, cell: texto(26) },
  { id: 'valor_novo', accessorFn: valorDoCampo('valor_novo'), header: 'Valor novo', size: 200, cell: texto(26) },
  { id: 'usuario', accessorKey: 'usuario', header: 'Usuário', size: 150, cell: texto(20) },
  { id: 'descricao', accessorFn: registro => [registro.descricao, resumirDetalhe(registro.tipo_acao, registro.detalhe)].filter(Boolean).join(' · '), header: 'Descrição', size: 320, cell: texto(48) }
];

const linhasDaResposta = resposta => (Array.isArray(resposta?.registros) ? resposta.registros : [])
  // Uma ação gera várias linhas (uma por campo): o id da tabela junta a ação e o campo.
  .map(registro => ({ ...registro, id: `${registro.auditoria_id}-${registro.campo || 'acao'}` }));

// dataProgramacao: dia selecionado na tela (aaaa-mm-dd). Com ele, abre só com a programação desse dia
// (cargas e importações da data); o botão alterna para todas as datas.
const ProgramacaoHavanAuditoriaModal = ({ show, onClose, dataProgramacao }) => {
  const [registros, setRegistros] = useState([]);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [carregando, setCarregando] = useState(false);
  const [filtros, setFiltros] = useState({});
  const [paginacao, setPaginacao] = useState(PAGINACAO_INICIAL);
  const [somenteDoDia, setSomenteDoDia] = useState(true);
  const dia = String(dataProgramacao || '').slice(0, 10);
  const filtrandoDia = somenteDoDia && Boolean(dia);

  // Cada abertura começa do zero (só o dia, sem filtros, primeira página): limpa ao fechar, para abrir com uma única busca.
  useEffect(() => {
    if (show) return;
    setFiltros({});
    setPaginacao(PAGINACAO_INICIAL);
    setSomenteDoDia(true);
  }, [show]);

  const alternarDia = useCallback(() => {
    setSomenteDoDia(atual => !atual);
    setPaginacao(atual => ({ ...atual, pageIndex: 0 }));
  }, []);

  const carregar = useCallback(async () => {
    if (!show) return;
    setCarregando(true);
    try {
      const resposta = await listarAuditoriaProgramacaoHavan({
        tipo_acao: filtros.tipo_acao,
        numero_carga: filtros.numero_carga,
        campo: filtros.campo,
        usuario: filtros.usuario,
        data_inicio: periodoParaApi(filtros.data_inicio),
        data_fim: periodoParaApi(filtros.data_fim, true),
        data_programacao: filtrandoDia ? dia : undefined,
        pagina: paginacao.pageIndex + 1,
        tamanho_pagina: paginacao.pageSize
      });
      setRegistros(linhasDaResposta(resposta));
      setTotalRegistros(resposta?.total_registros ?? 0);
    } catch (error) {
      notification.error(error?.response?.data?.error || 'Não foi possível carregar a auditoria da programação Havan.');
      setRegistros([]);
      setTotalRegistros(0);
    } finally {
      setCarregando(false);
    }
  }, [dia, filtrandoDia, filtros, paginacao.pageIndex, paginacao.pageSize, show]);

  useEffect(() => { carregar(); }, [carregar]);

  const aplicarFiltros = useCallback(proximos => {
    setFiltros(proximos || {});
    setPaginacao(atual => ({ ...atual, pageIndex: 0 }));
  }, []);

  const mudarPagina = useCallback(proxima => {
    setPaginacao(atual => {
      if (typeof proxima === 'function') return proxima(atual);
      if (typeof proxima === 'number') return { ...atual, pageIndex: proxima };
      if (proxima && typeof proxima === 'object') return { ...atual, ...proxima };
      return atual;
    });
  }, []);

  return (
    <Modal show={show} onHide={onClose} animation={false} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          {filtrandoDia ? `Auditoria da Programação Havan de ${formatarDataBr(dia)}` : 'Auditoria Geral da Programação Havan'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {dia ? (
          <EscopoAuditoria>
            <span>
              {filtrandoDia
                ? `Mostrando só a programação de ${formatarDataBr(dia)} (cargas e importações desse dia).`
                : 'Mostrando a auditoria de todas as datas.'}
            </span>
            <Button type="button" variant="outline-primary" size="sm" onClick={alternarDia} disabled={carregando}>
              {filtrandoDia ? 'Ver todas as datas' : `Ver só ${formatarDataBr(dia)}`}
            </Button>
          </EscopoAuditoria>
        ) : null}
        <FilterPanel
          filters={filtrosAuditoria}
          values={filtros}
          onApply={aplicarFiltros}
          title="Filtros de Auditoria"
          loading={carregando}
          defaultCollapsed
        />

        {registros.length === 0 && !carregando ? (
          <div className="text-muted mt-3">Nenhum registro de auditoria encontrado.</div>
        ) : (
          <TanStackTable
            key={`programacao-havan-auditoria-${paginacao.pageIndex}-${paginacao.pageSize}`}
            columns={colunasAuditoria}
            data={registros}
            loading={carregando}
            isSortable
            withPagination
            manualPagination
            totalItems={totalRegistros}
            controlledPagination={paginacao}
            onPaginationChange={mudarPagina}
            pageSizeOptions={[25, 50, 100]}
            enableColumnConfig
            columnConfigKey="programacao-havan-auditoria-colunas"
            enableExportExcel
            exportFileName="programacao-havan-auditoria"
            className="mt-3"
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Fechar</Button>
      </Modal.Footer>
    </Modal>
  );
};

ProgramacaoHavanAuditoriaModal.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  dataProgramacao: PropTypes.string
};

ProgramacaoHavanAuditoriaModal.defaultProps = {
  show: false,
  dataProgramacao: null
};

const EscopoAuditoria = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
  padding: 8px 12px;
  border: 1px solid #d7e0ea;
  border-radius: 4px;
  background: #f5f8fb;
  color: #24344d;
  font-size: 13px;
`;

export default ProgramacaoHavanAuditoriaModal;
